import { useQuery } from "@apollo/client";
import { withJsonFormsControlProps } from "@jsonforms/react";
import moment from "moment-timezone"; // Import moment-timezone
import { useContext, useEffect, useRef, useState } from "react";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import { listRewards } from "../../../graphql/queries/listRewards";
import AddCatalogRewards from "../../campaign/rulesAndOutcomes/outcomes/leaderboardOutcome/selectCatalogRewards/AddCatalogRewards";

const CatalogSelector = (props) => {
  const { handleChange, path, data, uischema } = props;
  const { programTimezone } = useContext(GlobalConfigContext);

  // Extract selection mode from UI schema (default to "multiple")
  const selectionMode = uischema?.options?.selection || "multiple";
  const isSingularMode = selectionMode === "singular";

  // Extract catalog IDs from the new structure
  const getCatalogIds = (catalogRefs) => {
    if (!catalogRefs || !Array.isArray(catalogRefs)) return [];
    return catalogRefs.map((ref) => ref.catalog_id);
  };

  // Store truly initial values in a ref so they don't change when data changes
  const initialValuesRef = useRef({
    rewardIds: data?.reward_ids || [],
    catalogRefs: data?.catalog_refs || [],
    catalogIds: getCatalogIds(data?.catalog_refs) || [],
  });

  // Flag to prevent initial state from being overwritten by form updates
  const initialSetupComplete = useRef(false);

  // Local state for selected catalogs and rewards
  const [selectedCatalogs, setSelectedCatalogs] = useState({
    rewards: [],
    catalogs: initialValuesRef.current.catalogIds.map((id) => ({
      id,
      displayText: id,
    })),
  });

  // Only fetch rewards once on initial load using the ref values
  const {
    data: initialRewardsData,
    loading,
    error,
  } = useQuery(listRewards, {
    variables: {
      filter: {
        isActive: true,
        catalogs: initialValuesRef.current.catalogIds,
      },
      page: 0,
      size: 1000,
    },
    fetchPolicy: "cache-and-network",
    // Skip query if no catalog IDs
    skip: initialValuesRef.current.catalogIds.length === 0,
  });

  // Initialize the rewards data on first load
  useEffect(() => {
    if (initialRewardsData?.listRewards?.items && !initialSetupComplete.current) {
      const allRewards = initialRewardsData.listRewards.items;

      // Format rewards for the AddCatalogRewards component
      const formattedRewards = allRewards
        .filter((reward) => initialValuesRef.current.rewardIds.includes(reward.id))
        .map((reward) => ({
          reward_id: reward.id,
          reward_name: reward.name,
          image_url: reward.imageUrl,
          description: reward.description || reward.shortDescription,
          quantity: reward.quantity,
          catalog_id: reward.catalogId,
        }));

      // Update the local state with the fetched rewards
      setSelectedCatalogs((prev) => ({
        ...prev,
        rewards: formattedRewards,
      }));

      // Mark initial setup as complete
      initialSetupComplete.current = true;
    }
  }, [initialRewardsData]);

  // Handle updates from AddCatalogRewards
  const updateSelectedCatalogs = (updatedCatalogs) => {
    try {
      // For singular mode, ensure only one reward is selected
      let processedCatalogs = updatedCatalogs;
      if (isSingularMode && updatedCatalogs.rewards?.length > 1) {
        // Keep only the last selected reward
        const lastReward =
          updatedCatalogs.rewards[updatedCatalogs.rewards.length - 1];
        processedCatalogs = {
          ...updatedCatalogs,
          rewards: [lastReward],
        };
      }

      // Update the local state first (for immediate UI response)
      setSelectedCatalogs(processedCatalogs);

      // Extract the reward_ids array from the processedCatalogs
      const newRewardIds =
        processedCatalogs.rewards?.map((reward) => reward.reward_id) || [];

      // Use a Set to collect unique catalog IDs from the rewards
      const catalogSet = new Set();
      processedCatalogs.rewards?.forEach((reward) => {
        if (reward.catalog_id) {
          catalogSet.add(reward.catalog_id);
        }
      });

      // Convert catalog IDs to the new structure with UTC timestamps
      const newCatalogRefs = Array.from(catalogSet).map((catalogId) => {
        // Check if this catalog_id already exists in the current catalog_refs
        const existingRef = (data?.catalog_refs || []).find(
          (ref) => ref.catalog_id === catalogId
        );

        // If it exists, keep the original attached_at timestamp
        if (existingRef) {
          return existingRef;
        }

        // Create new entry with current time converted to UTC based on program timezone
        const localTime = moment().tz(programTimezone || "UTC");
        return {
          catalog_id: catalogId,
          attached_at: localTime.utc().format(), // Format as ISO string in UTC
        };
      });

      // Update the form data
      handleChange(path, {
        ...data,
        reward_ids: newRewardIds,
        catalog_refs: newCatalogRefs,
      });
    } catch (error) {
      console.error("Error updating catalogs:", error);
    }
  };

  // Show loading indicator when fetching initial rewards
  if (loading && initialValuesRef.current.catalogIds.length > 0) {
    return <div>Loading reward details...</div>;
  }

  // Show error state if needed
  if (error && initialValuesRef.current.catalogIds.length > 0) {
    return <div>Error loading rewards. Please try again.</div>;
  }

  return (
    <AddCatalogRewards
      selectedCatalogs={selectedCatalogs}
      updateSelectedCatalogs={updateSelectedCatalogs}
      isSingularMode={isSingularMode}
      shouldSetDefaultValue
    />
  );
};
export { CatalogSelector };
export const CatalogSelectorControl = withJsonFormsControlProps(CatalogSelector);
